<template>
  <!--
    InsuranceInspector — the right pane (or bottom sheet) detail + action panel
    for a single selected insurance-catalog node. Purely presentational: driven
    by props (node / ancestors / childCount), communicates only through emits.
    NO Vuex, NO API. The page owns selection, placement and what each action
    actually does.

    Self-contained vertical block so it can sit in a wide right column OR a
    narrow bottom sheet without code changes — the parent controls placement.
  -->
  <section class="ins-inspector" aria-label="insurance node detail">
    <!-- EMPTY STATE — nothing selected ------------------------------------ -->
    <div v-if="!node" class="ins-inspector__empty">
      <span class="ins-inspector__empty-icon" aria-hidden="true">&#9737;</span>
      <p class="ins-inspector__empty-text">{{ $t("lmg.insure_select_to_view") }}</p>
    </div>

    <!-- DETAIL ------------------------------------------------------------ -->
    <div v-else class="ins-inspector__body">
      <!-- breadcrumb: root › … › parent › <node> (node name bold) -->
      <nav class="ins-inspector__crumbs" aria-label="breadcrumb">
        <span
          v-for="(crumb, i) in ancestors"
          :key="crumb.ID != null ? crumb.ID : i"
          class="ins-inspector__crumb"
          :title="crumb.Name"
        >
          <span class="ins-inspector__crumb-name">{{ displayName(crumb.Name, stripPrefix) }}</span>
          <span class="ins-inspector__crumb-sep" aria-hidden="true">&rsaquo;</span>
        </span>
        <span class="ins-inspector__crumb ins-inspector__crumb--current" :title="node.Name">
          <span class="ins-inspector__crumb-name is-current">{{ displayName(node.Name, stripPrefix) }}</span>
        </span>
      </nav>

      <!-- name (full real name exposed via title for hover discoverability) -->
      <h2 class="ins-inspector__name" :title="node.Name">{{ displayName(node.Name, stripPrefix) }}</h2>

      <!-- meta line: level · dataType chip · status -->
      <div class="ins-inspector__meta">
        <span class="ins-inspector__level">
          {{ $t("lmg.insure_level_n", { n: node.Level }) }}
        </span>

        <span
          class="ins-inspector__chip"
          :style="{ '--chip-color': meta.color }"
        >
          <span class="ins-inspector__chip-dot" aria-hidden="true"></span>
          <span class="ins-inspector__chip-label">{{ $t(meta.key) }}</span>
        </span>

        <span
          class="ins-inspector__status"
          :class="isActive ? 'is-on' : 'is-off'"
        >
          {{ isActive ? $t("lmg.insure_status_active") : $t("lmg.insure_status_inactive") }}
        </span>
      </div>

      <!-- child-count line -->
      <p class="ins-inspector__children">
        <template v-if="childCount > 0">
          {{ $t("lmg.insure_has_children_n", { n: childCount }) }}
        </template>
        <template v-else>
          {{ $t("lmg.insure_no_children") }}
        </template>
      </p>

      <!-- ACTIONS --------------------------------------------------------- -->
      <div class="ins-inspector__actions">
        <!-- add child — hidden at max depth (level 6) -->
        <calm-button
          v-if="canAddChild"
          variant="primary"
          size="sm"
          @click="$emit('add-child', node)"
        >
          {{ $t("lmg.insure_action_add_child") }}
        </calm-button>

        <!-- add sibling -->
        <calm-button
          variant="soft"
          size="sm"
          @click="$emit('add-sibling', node)"
        >
          {{ $t("lmg.insure_action_add_sibling") }}
        </calm-button>

        <!-- edit -->
        <calm-button
          variant="outline"
          size="sm"
          @click="$emit('edit', node)"
        >
          {{ $t("lmg.insure_action_edit") }}
        </calm-button>

        <!-- deactivate — only when active; disabled (with reason) if it has
             children, because the backend DELETE rejects parents with children.
             Pre-disabling avoids the error round-trip. -->
        <span
          v-if="isActive"
          class="ins-inspector__act-wrap"
          :title="hasChildren ? $t('lmg.insure_deactivate_blocked') : null"
        >
          <calm-button
            variant="danger"
            size="sm"
            :disabled="hasChildren"
            @click="$emit('deactivate', node)"
          >
            {{ $t("lmg.insure_action_deactivate") }}
          </calm-button>
        </span>

        <!-- reactivate — only when inactive -->
        <calm-button
          v-else
          variant="primary"
          size="sm"
          @click="$emit('reactivate', node)"
        >
          {{ $t("lmg.insure_action_reactivate") }}
        </calm-button>

        <!-- move — Phase 2; rendered disabled, never faked -->
        <span
          class="ins-inspector__act-wrap"
          :title="$t('lmg.insure_move_soon')"
        >
          <calm-button
            variant="ghost"
            size="sm"
            :disabled="true"
            @click="$emit('move', node)"
          >
            {{ $t("lmg.insure_action_move") }}
          </calm-button>
        </span>
      </div>
    </div>
  </section>
</template>

<script>
import { dataTypeMeta } from "./insDataType";
import { displayName } from "./insName";

const MAX_LEVEL = 6;

export default {
  name: "insurance-inspector",
  props: {
    // the selected node, or null when nothing is selected
    node: { type: Object, default: null },
    // root → parent chain, used to build the breadcrumb (node appended in view)
    ancestors: { type: Array, default: () => [] },
    // number of DIRECT children of `node`
    childCount: { type: Number, default: 0 },
    // DISPLAY-ONLY: hide leading outline numbers from shown names.
    stripPrefix: { type: Boolean, default: false },
  },
  methods: {
    displayName,
  },
  computed: {
    meta() {
      // safe even when node is null thanks to the v-if guard in template,
      // but keep it defensive for the computed-eval order.
      return dataTypeMeta(this.node ? this.node.DataType : 0);
    },
    isActive() {
      // IsActive may arrive as number 1/0 or string "1"/"0".
      return this.node ? Number(this.node.IsActive) === 1 : false;
    },
    hasChildren() {
      return Number(this.childCount) > 0;
    },
    canAddChild() {
      // hide "add child" once we hit the maximum tree depth
      return this.node ? Number(this.node.Level) < MAX_LEVEL : false;
    },
  },
};
</script>

<style scoped>
.ins-inspector {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  padding: 18px 20px;
  background: var(--c-surface, #fff);
  color: var(--c-text, #2c3e50);
  font-family: var(--c-font, inherit);
}

/* ---- empty state ---------------------------------------------------- */
.ins-inspector__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 160px;
  padding: 32px 16px;
  text-align: center;
}
.ins-inspector__empty-icon {
  font-size: 34px;
  line-height: 1;
  color: var(--c-border, #e4e9f1);
}
.ins-inspector__empty-text {
  margin: 0;
  font-size: 14px;
  color: var(--c-text-soft, #5e6c80);
}

/* ---- breadcrumb ----------------------------------------------------- */
.ins-inspector__crumbs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 0;
  font-size: 12px;
  color: var(--c-text-soft, #5e6c80);
  line-height: 1.4;
}
.ins-inspector__crumb {
  display: inline-flex;
  align-items: center;
  min-width: 0;
}
.ins-inspector__crumb-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 16ch;
}
.ins-inspector__crumb-name.is-current {
  font-weight: 700;
  color: var(--c-text, #2c3e50);
  max-width: none;
  white-space: normal;
}
.ins-inspector__crumb-sep {
  margin: 0 6px;
  color: var(--c-border, #e4e9f1);
}

/* ---- name ----------------------------------------------------------- */
.ins-inspector__name {
  margin: 10px 0 8px;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--c-text, #2c3e50);
  word-break: break-word;
}

/* ---- meta line ------------------------------------------------------ */
.ins-inspector__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-bottom: 10px;
}
.ins-inspector__level {
  font-size: 13px;
  color: var(--c-text-soft, #5e6c80);
}
.ins-inspector__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 9px;
  border-radius: 999px;
  background: var(--c-surface-soft, #fafbfd);
  border: 1px solid var(--c-border, #e4e9f1);
  font-size: 12px;
  color: var(--c-text-soft, #5e6c80);
  white-space: nowrap;
}
.ins-inspector__chip-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--chip-color, #8a96a8);
}
.ins-inspector__status {
  font-size: 13px;
  font-weight: 600;
}
.ins-inspector__status.is-on {
  color: var(--c-success, #38866b);
}
.ins-inspector__status.is-off {
  color: var(--c-text-soft, #5e6c80);
}

/* ---- child-count line ----------------------------------------------- */
.ins-inspector__children {
  margin: 0 0 18px;
  font-size: 13px;
  color: var(--c-text-soft, #5e6c80);
}

/* ---- actions -------------------------------------------------------- */
.ins-inspector__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
/* wrapper carries the title-tooltip so it works even when the button is
   disabled (a disabled <button> can swallow hover events in some browsers). */
.ins-inspector__act-wrap {
  display: inline-flex;
}
/* iPad touch targets — keep the sm Calm buttons at least 44px tall here. */
.ins-inspector__actions >>> .c-btn,
.ins-inspector__act-wrap >>> .c-btn {
  min-height: 44px;
}
</style>
